import type { ReactNode } from 'react'
import { Button } from '@/components/Button'
import {
  ModalSheet,
  ModalSheetHeader,
  ModalSheetTitle,
  ModalSheetContent,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeaderDescription,
  ModalSheetContentBlock,
  ModalSheetListItem,
} from '@/components/ModalSheet'
import styles from './BookkeepingCycleDetailsDialog.module.css'
import { formatToLongDateWithTime } from '@/features/jobs/bookkeeping/bookkeeping-cycle-formatters'
import type { CompletedWorkItem } from './BookkeepingCycleDetailsDialogViewModel'

export interface BookkeepingCycleDetailsDialogProps {
  showing: boolean
  onClose: () => unknown
  cycleNumber: number
  statusName: string
  startedAt: Date
  correctedAt?: Date
  completedAt?: Date
  isCompleted: boolean
  completedWorkItems: CompletedWorkItem[]
}

export function BookkeepingCycleDetailsDialog({
  showing,
  onClose,
  cycleNumber,
  statusName,
  startedAt,
  correctedAt,
  completedAt,
  isCompleted,
  completedWorkItems,
}: BookkeepingCycleDetailsDialogProps) {
  const completedWorkItemList = completedWorkItems.map((item, index) => (
    <BookkeepingServiceItemList key={index} item={item} />
  ))

  return (
    <ModalSheet
      showing={showing}
      onRequestClose={onClose}
      testId="cycle-details-dialog"
    >
      <ModalSheetHeader withCloseButton />
      <ModalSheetContent>
        <ModalSheetContentBlock>
          <ModalSheetTitle emphasized large testId="cycle-details-title">
            Bookkeeping cycle {cycleNumber} details
          </ModalSheetTitle>
          <ModalSheetHeaderDescription>
            Review details and service items included in the selected cycle.
          </ModalSheetHeaderDescription>
        </ModalSheetContentBlock>
      </ModalSheetContent>
      <ModalSheetContent>
        <BookkeepingCycleDetailItem
          label="Status"
          value={statusName}
          testId="cycle-details-status"
        />
        <BookkeepingCycleDetailItem
          label="Date started"
          value={formatToLongDateWithTime(startedAt)}
          testId="cycle-details-date-started"
        />
        {correctedAt && (
          <BookkeepingCycleDetailItem
            label="Date corrected"
            value={formatToLongDateWithTime(correctedAt)}
          />
        )}
        {completedAt && (
          <BookkeepingCycleDetailItem
            label="Date completed"
            value={formatToLongDateWithTime(completedAt)}
            testId="cycle-details-date-completed"
          />
        )}
        {isCompleted && (
          <BookkeepingCycleDetailItem
            label="Service items"
            value={completedWorkItemList}
            testId="cycle-details-service-items"
          />
        )}
      </ModalSheetContent>
      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            label="Close"
            variant="default"
            onPress={onClose}
            testId="cycle-details-close-button"
          />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
}

interface BookkeepingCycleDetailItemProps {
  label: string
  value: string | ReactNode
  testId?: string
}

function BookkeepingCycleDetailItem({
  label,
  value,
  testId,
}: BookkeepingCycleDetailItemProps) {
  return (
    <ModalSheetListItem>
      <div className={styles.cycleDetailItemContainer} data-test={testId}>
        <div>{label}</div>
        <div className={styles.cycleDetailItemValue}>{value}</div>
      </div>
    </ModalSheetListItem>
  )
}

interface BookkeepingServiceItemListProps {
  item: CompletedWorkItem
}

function BookkeepingServiceItemList({ item }: BookkeepingServiceItemListProps) {
  return (
    <div className={styles.bookkeepingServiceItemList}>
      {item.name} x{item.quantity}
    </div>
  )
}
